import React, { useState } from 'react';
import { FaTrash, FaUsers, FaClock, FaCommentDots } from 'react-icons/fa';

/**
 * Mobile-optimized Room Selection Modal
 * Compact, single-row entries designed for mobile screens
 */
const MobileRoomSelectionModal = ({ 
  isOpen, 
  onClose, 
  rooms, 
  narrativeName,
  onJoinRoom, 
  onDeleteRoom, 
  onCreateNew 
}) => {
  const [deletingRooms, setDeletingRooms] = useState(new Set());

  if (!isOpen) return null;

  const handleDeleteRoom = async (roomId, roomName) => {
    console.log('🗑️ MOBILE MODAL: Starting deletion process for:', roomId);
    
    // Ensure we stay in mobile mode during deletion
    sessionStorage.setItem('forceMobile', 'true');
    
    if (!window.confirm(`Delete "${roomName}"? This cannot be undone.`)) {
      console.log('🗑️ MOBILE MODAL: Deletion cancelled by user');
      return;
    }

    console.log('🗑️ MOBILE MODAL: User confirmed deletion, calling parent onDeleteRoom');
    setDeletingRooms(prev => new Set([...prev, roomId]));
    try {
      await onDeleteRoom(roomId);
      console.log('🗑️ MOBILE MODAL: Parent onDeleteRoom completed successfully');
    } catch (error) {
      console.error('🗑️ MOBILE MODAL: Error in deletion process:', error);
      alert('Failed to delete room. Please try again.');
    } finally {
      setDeletingRooms(prev => {
        const next = new Set(prev);
        next.delete(roomId);
        return next;
      });
      console.log('🗑️ MOBILE MODAL: Deletion process finished');
    }
  };

  const formatLastActivity = (lastActivity) => {
    if (!lastActivity) return 'Unknown';
    
    const now = new Date();
    const activity = new Date(lastActivity);
    const diffInHours = Math.floor((now - activity) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return '<1h';
    if (diffInHours < 24) return `${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden border border-gray-700">
        {/* Header */}
        <div className="bg-gray-800 border-b border-gray-700 p-4">
          <h2 className="text-lg font-semibold text-white">Continue {narrativeName}</h2>
          <p className="text-gray-400 text-sm mt-1">
            Choose an existing room to continue
          </p>
        </div>

        {/* Room List */}
        <div className="overflow-y-auto max-h-[70vh] p-3 bg-gray-900">
          {rooms.map((room) => (
            <div 
              key={room.id} 
              onClick={() => onJoinRoom(room)}
              className="bg-gray-800 border border-gray-700 rounded-lg p-3 mb-2 hover:bg-blue-900/30 hover:border-blue-600 transition-all cursor-pointer group"
            >
              {/* Header row with scene and delete */}
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-white text-sm truncate flex-1 group-hover:text-blue-200 transition-colors">
                  📖 Scene {room.scene_progress} • <strong>Tap to continue</strong>
                </h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click
                    handleDeleteRoom(room.id, room.name);
                  }}
                  className="text-gray-400 hover:text-red-400 p-1 ml-2 transition-colors"
                  disabled={deletingRooms.has(room.id)}
                  title="Delete room"
                >
                  {deletingRooms.has(room.id) ? (
                    <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaTrash className="w-3 h-3" />
                  )}
                </button>
              </div>
              
              {/* Stats row */}
              <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                <span className="flex items-center gap-1">
                  <FaUsers className="w-3 h-3" />
                  {room.participant_names?.length || 0}
                </span>
                <span className="flex items-center gap-1">
                  <FaCommentDots className="w-3 h-3" />
                  {room.message_count || 0}
                </span>
                <span className="flex items-center gap-1">
                  <FaClock className="w-3 h-3" />
                  {formatLastActivity(room.last_activity)}
                </span>
              </div>
              
              {/* Player names */}
              {room.participant_names && room.participant_names.length > 0 && (
                <div className="text-xs text-gray-400 mb-3 truncate">
                  Players: {room.participant_names.join(', ')}
                </div>
              )}
              
              {/* Join button - Keep as backup but make less prominent */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click
                  onJoinRoom(room);
                }}
                className="w-full bg-blue-600/50 hover:bg-blue-600 text-blue-200 hover:text-white py-2 rounded text-sm font-medium transition-colors border border-blue-500/30"
              >
                Continue Adventure
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 bg-gray-800 p-3">
          <div className="flex gap-2">
            <button
              onClick={onCreateNew}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded text-sm font-medium transition-colors"
            >
              New Adventure
            </button>
            <button
              onClick={onClose}
              className="px-4 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white py-2 rounded text-sm font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileRoomSelectionModal;